import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    IconButton,
    Divider,
    CircularProgress,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    ExpandMore as ExpandMoreIcon,
    CreditCard as CreditCardIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    AccountCircle as AccountIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useSettings } from '../contexts/SettingsContext';
import { safeInvoke } from '../utils/signalRHelper';
import AddressAutocomplete from './AddressAutocomplete';
import AccountAutocomplete from './AccountAutocomplete';
import { useGoogleMaps } from './GoogleMapsProvider';
import PaymentForm from './PaymentForm';
import { ridesAPI, carTypesAPI, paymentAPI, accountsAPI } from '../services/apiService';
import { isAirport, getAirportRules, formatPhoneNumber, PAYMENT_TYPE_LABELS, PRICING_DEFAULTS } from '../config/appConstants';

// Alias for backward compatibility
const SquarePaymentForm = PaymentForm;

// Convert carType to numeric value
const parseCarType = (carType) => {
    if (typeof carType === 'number') return carType;
    if (typeof carType === 'string') {
        const parsed = parseInt(carType);
        if (!isNaN(parsed)) return parsed;
    }
    return '';
};

const EditRideModal = ({ open, onClose, ride, onUpdate }) => {
    const { signalRConnection, connectSignalR } = useAuth();
    const { showAlert, showToast } = useAlert();
    const { settings, addOns } = useSettings();
    const { isLoaded: googleMapsLoaded } = useGoogleMaps();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
    const [addressesValidated, setAddressesValidated] = useState({ pickup: false, dropOff: false });
    const [stopsValidated, setStopsValidated] = useState([]);
    const [paymentTokenId, setPaymentTokenId] = useState(null);
    const [errors, setErrors] = useState({});
    const [carTypes, setCarTypes] = useState([]);
    const [linkedAccount, setLinkedAccount] = useState(null);
    // Ref always tracks the latest linkedAccount so async fullRecalc reads the correct value
    const linkedAccountRef = useRef(null);
    // Tracks the dollar amount discounted so it can be saved with the ride
    const discountAmountRef = useRef(0);
    // Track if CC accordion is expanded (user is entering card info)
    const [ccAccordionExpanded, setCcAccordionExpanded] = useState(false);
    const squarePaymentRef = useRef(null);
    // Track the original route. A *full* price recalculation only happens when the route itself
    // changes: pickup, drop-off, round trip, or car type. Stops, add-ons and schedule changes are
    // applied incrementally (add/remove the specific fee) so manual price edits are preserved.
    const [originalRoute, setOriginalRoute] = useState({ pickup: '', dropOff: '', roundTrip: false, carType: 0, scheduledFor: '' });
    // Rush hour surcharge currently baked into the price — used to apply only the delta when the schedule changes
    const [currentRushHourSurcharge, setCurrentRushHourSurcharge] = useState(0);
    // Number of stops currently reflected in the price — used to apply the stops delta incrementally
    const [pricedStopCount, setPricedStopCount] = useState(0);
    // Distance-based stops cost currently baked into the price — used for exact delta tracking
    const [currentStopsCost, setCurrentStopsCost] = useState(0);
    const [currentStopsDriverComp, setCurrentStopsDriverComp] = useState(0);

    // Form state
    const [formData, setFormData] = useState({
        // Customer info
        customerName: '',
        customerPhoneNumber: '',

        // Route info
        pickup: '',
        dropOff: '',
        stops: [],
        roundTrip: false,

        // Ride details
        scheduledFor: '',
        passengers: 1,
        carType: 0,
        selectedAddOnIds: [],
        flightNumber: '',
        notes: '',

        // Payment info
        paymentType: 'cash', // Default to cash
        cost: 0,
        driversCompensation: 0
    });

    // Initialize form data when ride prop changes
    useEffect(() => {
        // Fetch car types
        carTypesAPI.getAll().then(data => {
            setCarTypes((data || []).sort((a, b) => a.sortOrder - b.sortOrder));
        }).catch(err => console.error('Error fetching car types:', err));
    }, []);

    useEffect(() => {
        if (ride) {
            // Parse scheduled date/time for the datetime-local input
            // Note: ride.scheduledFor is UTC, but datetime-local needs local time format
            let scheduledDate = '';
            if (ride.scheduledFor) {
                // Append 'Z' if not present to ensure it's parsed as UTC
                const utcString = ride.scheduledFor.endsWith('Z') ? ride.scheduledFor : ride.scheduledFor + 'Z';
                const date = new Date(utcString);
                // Format as local datetime for the input (YYYY-MM-DDTHH:mm)
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                scheduledDate = `${year}-${month}-${day}T${hours}:${minutes}`;
            }

            // Extract stops from route
            const stops = [];
            if (ride.route) {
                for (let i = 1; i <= 10; i++) {
                    const stop = ride.route[`stop${i}`];
                    if (stop) stops.push(stop);
                }
            }

            setFormData({
                customerName: ride.customerName || '',
                customerPhoneNumber: ride.customerPhoneNumber || '',
                pickup: ride.route?.pickup || '',
                dropOff: ride.route?.dropOff || '',
                stops: stops,
                roundTrip: ride.route?.roundTrip || false,
                scheduledFor: scheduledDate,
                passengers: ride.passengers || 1,
                carType: parseCarType(ride.carType),
                selectedAddOnIds: ride.selectedAddOnIds
                    ? JSON.parse(ride.selectedAddOnIds)
                    : [],
                flightNumber: ride.flightNumber || '',
                notes: ride.notes || '',
                paymentType: ride.paymentType || (ride.paymentTokenId ? 'dispatcherCC' : 'cash'),
                cost: ride.cost || 0,
                driversCompensation: ride.driversCompensation || 0
            });

            // Mark addresses as validated since they come from the database
            setAddressesValidated({ pickup: true, dropOff: true });
            setStopsValidated(stops.map((_, i) => i));
            setPaymentTokenId(null);
            setErrors({});
            // Snapshot the original route. Only a change to one of these triggers a full reprice.
            setOriginalRoute({
                pickup: ride.route?.pickup || '',
                dropOff: ride.route?.dropOff || '',
                roundTrip: ride.route?.roundTrip || false,
                carType: parseCarType(ride.carType),
                scheduledFor: scheduledDate
            });
            // The loaded price already includes these stops, so seed the priced-stop count
            // and initialize the stops cost from the stored breakdown (null on old rides → treat as 0).
            setPricedStopCount(stops.length);
            setCurrentStopsCost(ride.stopsCost ?? 0);
            setCurrentStopsDriverComp(ride.stopsDriverComp ?? 0);
            // Fetch the baseline rush hour surcharge currently baked into the price (does NOT change cost),
            // so a later schedule change can apply just the rush hour difference.
            setCurrentRushHourSurcharge(0);
            if (ride.route?.pickup && ride.route?.dropOff) {
                ridesAPI.calculatePrice({
                    pickup: ride.route.pickup,
                    dropOff: ride.route.dropOff,
                    stops: stops,
                    carType: parseInt(parseCarType(ride.carType)) || 0,
                    isRoundTrip: ride.route?.roundTrip || false,
                    scheduledTime: ride.scheduledFor
                        ? (ride.scheduledFor.endsWith('Z') ? ride.scheduledFor : ride.scheduledFor + 'Z')
                        : null
                }).then(resp => {
                    if (resp?.success) setCurrentRushHourSurcharge(resp.rushHourSurcharge || 0);
                }).catch(() => { });
            }
            // Reset Square payment form when ride changes
            if (squarePaymentRef.current) {
                squarePaymentRef.current.reset();
            }

            // Load linked account (for discount reapplication)
            if (ride.accountId) {
                accountsAPI.getById(ride.accountId)
                    .then(acc => { setLinkedAccount(acc); linkedAccountRef.current = acc; })
                    .catch(() => { setLinkedAccount(null); linkedAccountRef.current = null; });
            } else {
                setLinkedAccount(null);
                linkedAccountRef.current = null;
            }
            // Seed the stored discount amount (trust what's in the DB)
            discountAmountRef.current = ride.accountDiscountAmount || 0;
        }
    }, [ride]);

    const handleAccountSelect = (account) => {
        linkedAccountRef.current = account;
        setLinkedAccount(account);
        setFormData(prev => {
            const updates = {};
            if (account.name) updates.customerName = account.name;
            if (!prev.customerPhoneNumber && account.phoneNumber) updates.customerPhoneNumber = account.phoneNumber;
            if (!prev.pickup && account.defaultPickup) updates.pickup = account.defaultPickup;
            if (!prev.dropOff && account.defaultDropoff) updates.dropOff = account.defaultDropoff;
            if (account.defaultCarType) updates.carType = account.defaultCarType;
            return { ...prev, ...updates };
        });
        // Trigger full recalc so discount is correctly applied via the ref
        fullRecalc();
    };

    const handleRemoveAccount = () => {
        linkedAccountRef.current = null;
        setLinkedAccount(null);
        // Trigger full recalc — ref is already null so discount won't be applied
        fullRecalc();
    };

    // FULL price recalculation — only used when the route itself changes
    // (pickup, drop-off, round trip, or car type). Prices the whole route via the server
    // (which uses company settings) and replaces cost + driver compensation entirely.
    const fullRecalc = async () => {
        if (!formData.pickup || !formData.dropOff) return;
        if (!addressesValidated.pickup || !addressesValidated.dropOff) return;

        setIsCalculatingPrice(true);
        try {
            const scheduledDateTime = formData.scheduledFor
                ? new Date(formData.scheduledFor).toISOString()
                : null;

            // Only include validated stops in the recalculated route
            const validatedStops = formData.stops.filter((stop, index) =>
                stop && stopsValidated.includes(index)
            );

            const response = await ridesAPI.calculatePrice({
                pickup: formData.pickup,
                dropOff: formData.dropOff,
                stops: validatedStops,
                carType: parseInt(formData.carType) || 0,
                isRoundTrip: formData.roundTrip,
                scheduledTime: scheduledDateTime
            });

            if (response.success) {
                // Add-on fees are added on top (they are not part of the route price)
                const addOnsTotal = (formData.selectedAddOnIds || []).reduce(
                    (sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0
                );
                const rawCost = Math.round(response.totalPrice) + addOnsTotal;
                // Reapply account discount (read from ref to get the value set synchronously before async call)
                let finalCost = rawCost;
                const account = linkedAccountRef.current;
                if (account && account.discountType && account.discountValue > 0) {
                    if (account.discountType === 'percent') {
                        finalCost = Math.max(0, Math.round(rawCost * (1 - Math.min(account.discountValue, 100) / 100)));
                    } else {
                        finalCost = Math.max(0, rawCost - account.discountValue);
                    }
                }
                // Compute and store the discount amount for saving
                discountAmountRef.current = Math.max(0, rawCost - finalCost);
                setFormData(prev => ({
                    ...prev,
                    // Add-on fees go entirely to the driver, so add them to both cost and driver compensation
                    cost: finalCost,
                    driversCompensation: Math.round(response.driversCompensation) + addOnsTotal
                }));
                // Keep incremental trackers in sync with the freshly priced route
                setCurrentRushHourSurcharge(response.rushHourSurcharge || 0);
                setPricedStopCount(validatedStops.length);
                setCurrentStopsCost(response.stopsCost || 0);
                setCurrentStopsDriverComp(response.stopsDriverComp || 0);
            } else {
                console.error('Pricing calculation failed:', response.error);
            }
        } catch (error) {
            console.error('Error calculating price:', error);
        } finally {
            setIsCalculatingPrice(false);
        }
    };

    // Adjust ONLY the rush hour surcharge when the scheduled time changes.
    // Fetches the surcharge for the new time and applies just the difference, preserving the base fare.
    const adjustRushHourForSchedule = async () => {
        if (!formData.pickup || !formData.dropOff) return;
        if (!addressesValidated.pickup || !addressesValidated.dropOff) return;

        setIsCalculatingPrice(true);
        try {
            const scheduledDateTime = formData.scheduledFor
                ? new Date(formData.scheduledFor).toISOString()
                : null;
            const validatedStops = formData.stops.filter((stop, index) =>
                stop && stopsValidated.includes(index)
            );

            const response = await ridesAPI.calculatePrice({
                pickup: formData.pickup,
                dropOff: formData.dropOff,
                stops: validatedStops,
                carType: parseInt(formData.carType) || 0,
                isRoundTrip: formData.roundTrip,
                scheduledTime: scheduledDateTime
            });

            if (response.success) {
                const newRush = response.rushHourSurcharge || 0;
                const rushDiff = newRush - currentRushHourSurcharge;
                if (rushDiff !== 0) {
                    setFormData(prev => ({
                        ...prev,
                        cost: Math.max(0, Math.round(prev.cost + rushDiff)),
                        driversCompensation: Math.max(0, Math.round(prev.driversCompensation + (rushDiff * PRICING_DEFAULTS.rushHourDriverCompRatio)))
                    }));
                    setCurrentRushHourSurcharge(newRush);
                }
            }
        } catch (error) {
            console.error('Error adjusting rush hour:', error);
        } finally {
            setIsCalculatingPrice(false);
        }
    };

    // Trigger a FULL recalculation only when the route changes: pickup, drop-off, round trip, or car type.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!formData.pickup || !formData.dropOff) return;
            if (!addressesValidated.pickup || !addressesValidated.dropOff) return;

            const routeChanged =
                formData.pickup !== originalRoute.pickup ||
                formData.dropOff !== originalRoute.dropOff ||
                !!formData.roundTrip !== !!originalRoute.roundTrip ||
                (parseInt(formData.carType) || 0) !== (parseInt(originalRoute.carType) || 0);

            if (routeChanged) {
                fullRecalc();
                // Re-baseline so we don't repeatedly recalc for the same change
                setOriginalRoute(prev => ({
                    ...prev,
                    pickup: formData.pickup,
                    dropOff: formData.dropOff,
                    roundTrip: formData.roundTrip,
                    carType: formData.carType
                }));
            }
        }, 500); // Debounce for 500ms

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.pickup, formData.dropOff, formData.roundTrip, formData.carType, addressesValidated]);

    // Incrementally recalculate only the stops cost when stops change (no full recalc).
    // Calls the server's CalculateStopsCost endpoint to get distance-aware pricing for all stops,
    // then applies the delta vs. the previously priced stops cost.
    useEffect(() => {
        if (!formData.pickup || !formData.dropOff) return;
        if (!addressesValidated.pickup || !addressesValidated.dropOff) return;

        const validatedStops = formData.stops.filter((stop, index) =>
            stop && stopsValidated.includes(index)
        );
        const validatedStopCount = validatedStops.length;

        // No change in validated stop count — nothing to do
        if (validatedStopCount === pricedStopCount) return;

        let cancelled = false;
        const calculateStops = async () => {
            try {
                const response = await ridesAPI.calculateStopsCost({
                    pickup: formData.pickup,
                    dropOff: formData.dropOff,
                    stops: validatedStops,
                });
                if (cancelled) return;
                if (response.success) {
                    const newStopsCost = response.stopsCost || 0;
                    const newStopsDriverComp = response.stopsDriverComp || 0;
                    const costDelta = newStopsCost - currentStopsCost;
                    const compDelta = newStopsDriverComp - currentStopsDriverComp;
                    setFormData(prev => ({
                        ...prev,
                        cost: Math.max(0, Math.round(prev.cost + costDelta)),
                        driversCompensation: Math.max(0, Math.round(prev.driversCompensation + compDelta))
                    }));
                    setCurrentStopsCost(newStopsCost);
                    setCurrentStopsDriverComp(newStopsDriverComp);
                    setPricedStopCount(validatedStopCount);
                }
            } catch (err) {
                console.error('Error calculating stops cost:', err);
            }
        };
        calculateStops();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.stops, stopsValidated]);

    // Adjust only the rush hour fee when the scheduled time changes (no full recalc).
    useEffect(() => {
        // Skip the initial mount / when schedule still matches the original
        if (!formData.scheduledFor || formData.scheduledFor === originalRoute.scheduledFor) return;

        const timer = setTimeout(() => {
            adjustRushHourForSchedule();
            setOriginalRoute(prev => ({ ...prev, scheduledFor: formData.scheduledFor }));
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.scheduledFor]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddressChange = (field, value, isFromAutocomplete = false) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (isFromAutocomplete) {
            setAddressesValidated(prev => ({ ...prev, [field]: true }));
        } else {
            // If user types manually, mark as not validated
            setAddressesValidated(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleAddStop = () => {
        if (formData.stops.length < 10) {
            setFormData(prev => ({ ...prev, stops: [...prev.stops, ''] }));
        }
    };

    const handleRemoveStop = (index) => {
        setFormData(prev => ({
            ...prev,
            stops: prev.stops.filter((_, i) => i !== index)
        }));
        // Keep validated-stop indices in sync with the new array positions
        setStopsValidated(prev => prev
            .filter(i => i !== index)
            .map(i => (i > index ? i - 1 : i))
        );
    };

    const handleStopChange = (index, value, isFromAutocomplete = false) => {
        setFormData(prev => {
            const newStops = [...prev.stops];
            newStops[index] = value;
            return { ...prev, stops: newStops };
        });
        if (isFromAutocomplete) {
            setStopsValidated(prev => {
                if (!prev.includes(index)) {
                    return [...prev, index];
                }
                return prev;
            });
        } else {
            // Remove from validated if user types manually
            setStopsValidated(prev => prev.filter(i => i !== index));
        }
    };

    // Handle Square payment token
    const handlePaymentTokenGenerated = async (nonce) => {
        try {
            // Exchange the short-lived nonce for a permanent Square card on file.
            // Nonces expire in ~24 hours; card IDs (ccof:...) never expire.
            const customerName = formData.customerName || 'Unknown';
            const result = await paymentAPI.saveCardOnFile(nonce, customerName);
            if (result.success) {
                setPaymentTokenId(result.cardId);
                setErrors(prev => ({ ...prev, paymentTokenId: '' }));
                showToast(`${result.cardBrand} ending in ${result.last4} saved on file`, 'success');
            } else {
                setErrors(prev => ({ ...prev, paymentTokenId: result.message }));
                showAlert('Error', `Failed to save card: ${result.message}`, [{ text: 'OK' }], 'error');
            }
        } catch (err) {
            console.error('❌ SaveCardOnFile error:', err);
            setErrors(prev => ({ ...prev, paymentTokenId: 'Failed to save card on file' }));
            showAlert('Error', 'Failed to save card on file. Please try again.', [{ text: 'OK' }], 'error');
        }
    };

    const handleTokenError = (error) => {
        console.error('Payment token error:', error);
        setErrors(prev => ({ ...prev, paymentTokenId: error }));
    };

    // Phone number formatting - use shared utility
    // (formatPhoneNumber imported from appConstants)

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        handleInputChange('customerPhoneNumber', formatted);
    };

    // Validate all required fields before saving
    const validateForm = () => {
        const newErrors = {};

        // Phone number is required
        if (!formData.customerPhoneNumber?.trim()) {
            newErrors.customerPhoneNumber = 'Phone number is required';
        }

        // Pickup is required
        if (!formData.pickup?.trim()) {
            newErrors.pickup = 'Pickup location is required';
        }

        // Drop-off is required
        if (!formData.dropOff?.trim()) {
            newErrors.dropOff = 'Drop-off location is required';
        }

        // Price must have a value (can be 0 but not empty/null)
        const costValue = parseFloat(formData.cost);
        if (formData.cost === '' || formData.cost === null || formData.cost === undefined || isNaN(costValue)) {
            newErrors.cost = 'Price is required';
        }

        // Driver compensation should have a value too
        const driverCompValue = parseFloat(formData.driversCompensation);
        if (formData.driversCompensation === '' || formData.driversCompensation === null || formData.driversCompensation === undefined || isNaN(driverCompValue)) {
            newErrors.driversCompensation = 'Driver compensation is required';
        }

        // No payment validation needed - CC is optional, driver handles payment at dropoff

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!signalRConnection || signalRConnection.state !== 'Connected') {
            showAlert('Connection Error', 'SignalR not connected. Please refresh the page.', [{ text: 'OK' }], 'error');
            return;
        }

        // Validate all fields before submitting
        if (!validateForm()) {
            showToast('Please fix the errors before saving', 'error');
            return;
        }

        // If CC accordion is expanded but card not tokenized (and no existing token), show error
        if (ccAccordionExpanded && !paymentTokenId && !ride.paymentTokenId) {
            showAlert(
                'Card Not Saved',
                'You have the credit card form open but haven\'t saved the card yet.\n\nPlease either:\n• Click "Save Card" to save the card\n• Or collapse the credit card section if you don\'t want to add a card',
                [{ text: 'OK' }],
                'warning'
            );
            return;
        }

        setIsSubmitting(true);

        try {
            // Determine payment type and paid status
            // If a new token was added, or ride already has a token, it's card on file
            const hasPaymentToken = paymentTokenId || ride.paymentTokenId;
            // Use selected payment type, override to dispatcherCC if there's a token
            const effectivePaymentType = hasPaymentToken ? 'dispatcherCC' : (formData.paymentType || 'cash');
            // Paid = true for Zelle (already paid) or if CC token exists (payment secured)
            const isPaid = effectivePaymentType === 'zelle' || !!hasPaymentToken;

            // Build the update object
            const updateData = {
                rideId: ride.rideId,
                customerName: formData.customerName,
                customerPhoneNumber: formData.customerPhoneNumber,
                scheduledFor: formData.scheduledFor ? new Date(formData.scheduledFor).toISOString() : null,
                passengers: parseInt(formData.passengers) || 1, // Default to 1 if blank
                carType: parseInt(formData.carType) || 0,
                selectedAddOnIds: formData.selectedAddOnIds || [],
                flightNumber: formData.flightNumber,
                notes: formData.notes,
                paymentType: effectivePaymentType,
                paid: isPaid,
                cost: parseFloat(formData.cost) || 0,
                driversCompensation: parseFloat(formData.driversCompensation) || 0,
                // Route data
                pickup: formData.pickup,
                dropOff: formData.dropOff,
                stops: formData.stops.filter(s => s), // Filter out empty stops
                roundTrip: formData.roundTrip,
                // Payment token (if newly provided)
                paymentTokenId: paymentTokenId || ride.paymentTokenId,
                accountId: linkedAccount ? linkedAccount.accountId : 0, // 0 signals "clear the account"
                accountDiscountAmount: discountAmountRef.current,
                // Stops pricing breakdown (distance-based)
                stopsCost: currentStopsCost,
                stopsDriverComp: currentStopsDriverComp
            };

            // Call SignalR to update the ride
            const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'RideUpdated',
                args: [updateData],
                onRetrying: (attempt, max) => {
                    showToast(`Retrying... (${attempt}/${max})`, 'info');
                }
            });

            if (!result.success) {
                throw new Error(result.error);
            }

            showToast('Ride updated successfully', 'success');
            onUpdate && onUpdate();
            onClose();
        } catch (error) {
            console.error('Failed to update ride:', error);
            showAlert('Error', `Failed to update ride: ${error.message}`, [{ text: 'OK' }], 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!ride) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        Edit Ride #{ride.rideId}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Customer Information */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Customer Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <AccountAutocomplete
                            label="Customer Name"
                            value={formData.customerName}
                            onChange={(text) => handleInputChange('customerName', text)}
                            onAccountSelect={handleAccountSelect}
                            linkedAccount={linkedAccount}
                            onRemoveAccount={handleRemoveAccount}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.customerPhoneNumber}
                            onChange={handlePhoneChange}
                            placeholder="(555) 555-5555"
                            error={!!errors.customerPhoneNumber}
                            helperText={errors.customerPhoneNumber}
                            margin="normal"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* Route Information */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Route Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        {googleMapsLoaded ? (
                            <AddressAutocomplete
                                label="Pickup Address"
                                value={formData.pickup}
                                onChange={(value) => handleAddressChange('pickup', value, false)}
                                onSelect={(value) => handleAddressChange('pickup', value, true)}
                                error={!!errors.pickup}
                                helperText={errors.pickup}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Pickup Address"
                                value={formData.pickup}
                                onChange={(e) => handleAddressChange('pickup', e.target.value, false)}
                                error={!!errors.pickup}
                                helperText={errors.pickup}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {googleMapsLoaded ? (
                            <AddressAutocomplete
                                label="Drop-off Address"
                                value={formData.dropOff}
                                onChange={(value) => handleAddressChange('dropOff', value, false)}
                                onSelect={(value) => handleAddressChange('dropOff', value, true)}
                                error={!!errors.dropOff}
                                helperText={errors.dropOff}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Drop-off Address"
                                value={formData.dropOff}
                                onChange={(e) => handleAddressChange('dropOff', e.target.value, false)}
                                error={!!errors.dropOff}
                                helperText={errors.dropOff}
                            />
                        )}
                    </Grid>

                    {/* Stops */}
                    {formData.stops.map((stop, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <Box display="flex" gap={1} alignItems="center">
                                {googleMapsLoaded ? (
                                    <AddressAutocomplete
                                        label={`Stop ${index + 1}`}
                                        value={stop}
                                        onChange={(value) => handleStopChange(index, value, true)}
                                        sx={{ flex: 1 }}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        label={`Stop ${index + 1}`}
                                        value={stop}
                                        onChange={(e) => handleStopChange(index, e.target.value, false)}
                                        sx={{ flex: 1 }}
                                    />
                                )}
                                <IconButton
                                    onClick={() => handleRemoveStop(index)}
                                    color="error"
                                    size="small"
                                >
                                    <RemoveIcon />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={3}>
                            {formData.stops.length < 10 && (
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddStop}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Stop
                                </Button>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.roundTrip}
                                        onChange={(e) => handleInputChange('roundTrip', e.target.checked)}
                                    />
                                }
                                label="Round Trip"
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* Schedule & Ride Details */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Schedule & Details
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Scheduled For"
                            value={formData.scheduledFor}
                            onChange={(e) => handleInputChange('scheduledFor', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    {isAirport(formData.pickup, getAirportRules(settings)) && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Flight Number"
                                value={formData.flightNumber}
                                onChange={(e) => handleInputChange('flightNumber', e.target.value)}
                                placeholder="e.g., AA1234"
                            />
                        </Grid>
                    )}
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Passengers"
                            value={formData.passengers}
                            onChange={(e) => handleInputChange('passengers', e.target.value)}
                            inputProps={{ min: 1, max: 15 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth>
                            <InputLabel>Car Type</InputLabel>
                            <Select
                                value={formData.carType}
                                label="Car Type"
                                onChange={(e) => handleInputChange('carType', e.target.value)}
                            >
                                {carTypes.map(type => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {addOns.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Additional Requirements</Typography>
                                {addOns.map(addOn => (
                                    <FormControlLabel
                                        key={addOn.id}
                                        control={
                                            <Checkbox
                                                checked={(formData.selectedAddOnIds || []).includes(addOn.id)}
                                                onChange={(e) => {
                                                    const current = formData.selectedAddOnIds || [];
                                                    const updated = e.target.checked
                                                        ? [...current, addOn.id]
                                                        : current.filter(id => id !== addOn.id);
                                                    const diff = e.target.checked ? addOn.fee : -addOn.fee;
                                                    // Add-on fee goes entirely to the driver
                                                    setFormData(prev => ({ ...prev, selectedAddOnIds: updated, cost: Math.max(0, prev.cost + diff), driversCompensation: Math.max(0, prev.driversCompensation + diff) }));
                                                }}
                                                size="small"
                                            />
                                        }
                                        label={`${addOn.name} (+$${addOn.fee})`}
                                        sx={{ display: 'block' }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Notes"
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                            placeholder="Additional notes for the driver..."
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* Payment Information */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Payment Information
                    </Typography>
                    {/* Payment Status Badge */}
                    {(ride.paid || ride.paymentTokenId || paymentTokenId) ? (
                        <Chip
                            icon={<CheckCircleIcon />}
                            label={formData.paymentType === 'zelle' ? 'Zelle' : 'Card on File'}
                            color="success"
                            size="small"
                        />
                    ) : (
                        <Chip
                            icon={<WarningIcon />}
                            label={formData.paymentType === 'driverCC' ? 'Driver CC' : 'Cash'}
                            color="warning"
                            size="small"
                        />
                    )}
                </Box>

                {/* Payment Type Selector */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="edit-payment-type-label">Payment Method</InputLabel>
                    <Select
                        labelId="edit-payment-type-label"
                        value={formData.paymentType || 'cash'}
                        label="Payment Method"
                        onChange={(e) => {
                            const value = e.target.value;
                            handleInputChange('paymentType', value);
                            // If not dispatcherCC, clear card info
                            if (value !== 'dispatcherCC') {
                                setPaymentTokenId(null);
                                if (squarePaymentRef.current) {
                                    squarePaymentRef.current.reset();
                                }
                            }
                        }}
                    >
                        {['cash', 'zelle', 'driverCC', 'dispatcherCC'].map(method => (
                            <MenuItem key={method} value={method}>{PAYMENT_TYPE_LABELS[method] || method}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Payment type description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formData.paymentType === 'cash' && 'Driver will collect cash from the customer at dropoff.'}
                    {formData.paymentType === 'zelle' && 'Customer has already paid via Zelle. No payment action needed.'}
                    {formData.paymentType === 'driverCC' && 'Driver will collect credit card details from the customer at dropoff.'}
                    {formData.paymentType === 'dispatcherCC' && (ride.paymentTokenId || paymentTokenId) && 'Card is on file. Driver will charge it at dropoff.'}
                    {formData.paymentType === 'dispatcherCC' && !(ride.paymentTokenId || paymentTokenId) && 'Enter customer card below to save on file.'}
                    {!formData.paymentType && 'Select a payment method above.'}
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Cost"
                            value={formData.cost}
                            onChange={(e) => handleInputChange('cost', e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: isCalculatingPrice ? <CircularProgress size={20} /> : null
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            error={!!errors.cost}
                            helperText={errors.cost}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Driver Compensation"
                            value={formData.driversCompensation}
                            onChange={(e) => handleInputChange('driversCompensation', e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: isCalculatingPrice ? <CircularProgress size={20} /> : null
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            error={!!errors.driversCompensation}
                            helperText={errors.driversCompensation}
                        />
                    </Grid>

                    {/* Collapsible Credit Card Section - Only show for dispatcherCC */}
                    {formData.paymentType === 'dispatcherCC' && (
                        <Grid item xs={12}>
                            <Accordion
                                expanded={ccAccordionExpanded && !paymentTokenId}
                                onChange={(e, expanded) => setCcAccordionExpanded(expanded)}
                                TransitionProps={{ unmountOnExit: true }}
                                sx={{
                                    mt: 2,
                                    '&:before': { display: 'none' },
                                    boxShadow: 'none',
                                    border: '1px solid',
                                    borderColor: (ride.paymentTokenId || paymentTokenId) ? 'success.main' : 'divider',
                                    borderRadius: '8px !important',
                                    bgcolor: (ride.paymentTokenId || paymentTokenId) ? 'success.lighter' : 'background.paper'
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        '& .MuiAccordionSummary-content': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }
                                    }}
                                >
                                    <CreditCardIcon color={(ride.paymentTokenId || paymentTokenId) ? 'success' : 'action'} />
                                    <Typography>
                                        {(ride.paymentTokenId || paymentTokenId)
                                            ? 'Credit Card on File ✓'
                                            : 'Add Credit Card'}
                                    </Typography>
                                    {(ride.paymentTokenId || paymentTokenId) && (
                                        <Chip
                                            label="Card on File"
                                            size="small"
                                            color="success"
                                            sx={{ ml: 'auto', mr: 1 }}
                                        />
                                    )}
                                </AccordionSummary>
                                <AccordionDetails>
                                    {ride.paymentTokenId ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            A credit card is already on file. You can enter a new card below to replace it.
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Enter credit card details to prepay. Card will be tokenized securely (no raw card data is stored).
                                        </Typography>
                                    )}

                                    <PaymentForm
                                        ref={squarePaymentRef}
                                        onTokenGenerated={handlePaymentTokenGenerated}
                                        onTokenCleared={() => {
                                            setPaymentTokenId(null);
                                            setCcAccordionExpanded(true);
                                        }}
                                        onError={handleTokenError}
                                    />

                                    {errors.paymentTokenId && (
                                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                            {errors.paymentTokenId}
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'Updating...' : 'Update Ride'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRideModal;
